import { Fragment, useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import { v4 as uuidv4 } from 'uuid';
import {
  Select,
  ButtonPrimary,
  ButtonSize,
  Popup,
  ButtonPseudo,
  InputText,
  IconEdit,
  IconInfo,
  IconChain,
  ButtonSecondary,
  IconExpand,
  PermissionEnum,
  IconLoader,
  ToggleInput,
  Currency,
  Checkbox,
  InputNumber,
} from '@ui/src';
import { useAppSelector } from '@store';
import PspIntegrationItem from './psp-integration-item';
import { PspIntegrationsTypes } from './types';
import Chain from './chain';
import { ExternalVaultSettings } from '../../hoc/bank-and-psp/types';
import CurrencySettingItem from './currency-setting-item';
import FeeSettingItem from './fee-setting-item';
import useGetExternalAccounts from '../../hoc/bank-and-psp/helpers/use-get-external-accounts';

import cn from './info.module.css';

const getPspInegtrationName = (name: string) => {
  const nameArray = name.split('_');

  let nextName;

  if (nameArray.length > 1) {
    nextName = nameArray
      .map((item) => `${item.charAt(0)}${item.slice(1).toLocaleLowerCase()}`)
      .join(' ');
  } else {
    nextName = nameArray.join('');
  }

  return nextName;
};

const PspIntegrations = ({
  pspIntegrations,
  isLoading,
  selectedPspIntegration,
  setSelectedPspIntegration,
  integrated,
  applyInProgress,
  onApplyPspIntegration,
  applyIsSuccess,
  sortCode,
  setSortCode,
  bic,
  setBic,
  onAddPaymentSettings,
  paymentSettingsSuccess,
  addPaymentSettingsIsLoading,
  paymentsSettings,
  onUpdatePaymentSettings,
  updatePaymentSettingsInProgress,
  sortCodeError,
  setSortCodeError,
  isEditable,
  paymentTypes,
  casp,
  caspChainsIsLoading,
  chains,
  setChains,
  applyChainsMappingInProgress,
  onApplyCaspMapping,
  setAddChainIsVisible,
  addChainIsVisible,
  setDeletePopupVisible,
  setSelectedChainId,
  externalVaultSettings,
  onUpdateExternalVaultSettings,
  updateExternalSettingsIsLoading,
  bankId,
  updateExternalSettinsAction,
  chainCreationErrors,
  setChainCreationErrors,
  chainsList,
  configureCASPCurrenciesVisible,
  setConfigureCASPCurrenciesVisible,
  currencies,
  globalExternalVaultSettingsError,
  globalExternalCurrenciesSettingsError,
  currenciesIsRequiredError,
  setCurrenciesIsRequiredError,
  currencyCASPRequiredErrors,
  setCurrencyCASPRequiredErrors,
  setGlobalExternalCurrenciesSettingsError,
  setGlobalExternalVaultSettingsError,
  outgoingTransactionsFeeSettings,
  nostroTransactionsFeeSettings,
  setFeeSettingsPanelIsOpen,
  setSelectedTransactionType,
  setDeleteFeeSettingsPopupVisible,
  feeSettingsIsLoading,
  getFeeTierItemAction,
  setSelectedFeeTierItemId,
  exchangeRateTolerance,
  setExchangeRateTolerance,
  updateExcahngeRateToleranceInProgress,
  onUpdateExchangeRateTolerance,
  globalToleranceError,
  toleranceErrors,
  clearErrors,
  setToleranceError,
}: PspIntegrationsTypes) => {
  const [integrationPopupVisible, setIntegrationPopupVisible] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<
    { id: string; name: string } | undefined
  >(undefined);
  const [sortCodePopupVisible, setSortCodePopupVisible] = useState(false);
  const [paymentSettingsId, setPaymentSettingsId] = useState('');
  const [availableCurrencies, setAvilableCurrencies] = useState<Currency[]>([]);

  const { activePermissions } = useAppSelector(
    (state) => state.configurationDictionaries,
  );

  const linkingSettingsIsDirty = !isEqual(
    {
      ...externalVaultSettings,
      externalAccountsAutoCreateCurrencies:
        externalVaultSettings?.externalAccountsAutoCreateCurrencies?.map(
          (item) => ({ currencyId: item.currencyId, caspId: item.caspId }),
        ),
    },
    {
      externalAccountsAutoCreate:
        selectedPspIntegration?.externalAccountsAutoCreate,
      externalPspPartnerAccountCreate:
        selectedPspIntegration?.externalPspPartnerAccountCreate,
      externalAccountsAutoCreateCurrencyType:
        selectedPspIntegration?.externalAccountsAutoCreateCurrencyType,
      externalAccountsAutoCreateCurrencies:
        selectedPspIntegration?.externalAccountsAutoCreateCurrencies,
    },
  );

  const { autocompleteList } = useGetExternalAccounts(
    `&pspPartnerId=${bankId}`,
  );

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    let inputValue = sortCode;

    if (e.key !== 'Backspace') {
      const regex = /.{1,2}/g;

      inputValue = sortCode
        ?.replace(/[^0-9]/g, '')
        ?.match(regex)
        ?.join('-');
    }

    setSortCode(inputValue as string);
  };

  useEffect(() => {
    if (selectedPspIntegration) {
      const integrationCurrencies: Currency[] = [];

      selectedPspIntegration?.pspIntegrationPayments?.forEach((item) => {
        item.currencies?.forEach((currency) => {
          if (
            !integrationCurrencies?.find(
              (integrationCurrency) =>
                integrationCurrency.id === currency.currency.id,
            ) &&
            currencies?.find(
              (currencyItem) => currencyItem.id === currency.currency.id,
            )
          ) {
            integrationCurrencies.push(
              currencies?.find(
                (currencyItem) => currencyItem.id === currency.currency.id,
              ) as Currency,
            );
          }
        });
      });

      setAvilableCurrencies(integrationCurrencies);
    }
  }, [selectedPspIntegration, currencies]);

  useEffect(() => {
    if (applyIsSuccess) {
      setIntegrationPopupVisible(false);
    }
  }, [applyIsSuccess]);

  useEffect(() => {
    if (paymentSettingsSuccess) {
      setSortCodePopupVisible(false);
      setSortCode('');
      setBic('');
      setSelectedGroup(undefined);
      setPaymentSettingsId('');
      setSortCodeError('');
    }
  }, [paymentSettingsSuccess]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.integrationLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <>
        <div className={cn.integrationBlock}>
          <Select
            name="pspIntegration"
            placeholder="Integration type"
            value={selectedPspIntegration?.id || ''}
            title="Select integration type"
            list={pspIntegrations?.map((item) => ({
              name: getPspInegtrationName(item.name),
              value: item.id,
            }))}
            onChange={(e) => {
              const currentPspIntegrations = pspIntegrations?.find(
                (item) => item.id === e.target.value,
              );

              if (currentPspIntegrations) {
                setSelectedPspIntegration(currentPspIntegrations);
              }
            }}
            disabled={!isEditable}
          />
        </div>

        {selectedPspIntegration && (
          <div className={cn.pspIntegrationsWrapper}>
            {(selectedPspIntegration?.name === 'PRIME_VAULT_WALLET' ||
              selectedPspIntegration?.name === 'OPEN_PAYD' ||
              selectedPspIntegration?.name === 'B2C2') && (
              <>
                {(selectedPspIntegration?.name === 'PRIME_VAULT_WALLET' ||
                  selectedPspIntegration?.name === 'B2C2') && (
                  <>
                    {activePermissions?.includes(
                      PermissionEnum.PSP_INTEGRATION_CASPS_CHAINS_LIST,
                    ) && (
                      <div className={cn.caspChainsWrapper}>
                        <div className={cn.caspChainsTitle}>
                          CASP Connections for Accounts Opening
                          <div className={cn.caspChainsTitleIcon}>
                            <IconInfo width={16} height={16} />
                            <div className={cn.caspChainsTitleHint}>
                              Map system CASP protocols to provider-specific
                              chains to enable account opening and transactions
                              for this partner.
                            </div>
                          </div>
                        </div>
                        <div className={cn.caspChains}>
                          {caspChainsIsLoading && (
                            <div className={cn.caspChainsLoader}>
                              <IconLoader />
                            </div>
                          )}
                          {!caspChainsIsLoading && (
                            <>
                              {!addChainIsVisible && (
                                <>
                                  <div className={cn.emptyChainsIcon}>
                                    <IconChain />
                                  </div>
                                  <div className={cn.emptyChainsText}>
                                    No CASP mappings defined yet. Add mappings
                                    to connect internal CASP IDs with provider
                                    chains
                                  </div>
                                  {activePermissions?.includes(
                                    PermissionEnum.PSP_INTEGRATION_CASPS_CHAINS_CREATE_ACTION,
                                  ) && (
                                    <div className={cn.emptyChainsControls}>
                                      <ButtonSecondary
                                        size={ButtonSize.SMALL}
                                        text="Add mapping rule"
                                        icon={
                                          <IconExpand width={16} height={16} />
                                        }
                                        className={`button-white ${cn.addMappingButton}`}
                                        onClick={() => {
                                          setAddChainIsVisible(true);
                                          setChains([
                                            {
                                              id: 'chain-1',
                                              caspId: '',
                                              chain: '',
                                            },
                                          ]);
                                        }}
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                              {addChainIsVisible && (
                                <>
                                  <div className={cn.chains}>
                                    {chains?.map((item) => (
                                      <Chain
                                        key={item.id}
                                        chain={item}
                                        setChains={setChains}
                                        chains={chains}
                                        casp={casp}
                                        setAddChainIsVisible={
                                          setAddChainIsVisible
                                        }
                                        activePermissions={activePermissions}
                                        setDeletePopupVisible={
                                          setDeletePopupVisible
                                        }
                                        setSelectedChainId={setSelectedChainId}
                                        chainCreationErrors={chainCreationErrors?.find(
                                          (error) => item.id === error.id,
                                        )}
                                        chainCreationErrorsList={
                                          chainCreationErrors
                                        }
                                        setChainCreationErrors={
                                          setChainCreationErrors
                                        }
                                        chainsList={chainsList}
                                      />
                                    ))}
                                  </div>
                                  {activePermissions?.includes(
                                    PermissionEnum.PSP_INTEGRATION_CASPS_CHAINS_CREATE_ACTION,
                                  ) && (
                                    <div className={cn.chainsControls}>
                                      <ButtonPseudo
                                        size={ButtonSize.SMALL}
                                        text="Add mapping rule"
                                        className={`button-white ${cn.addNewChainButton}`}
                                        icon={<IconExpand />}
                                        onClick={() => {
                                          const nextChains = (
                                            [] as {
                                              id: string;
                                              caspId: string;
                                              chain: string;
                                            }[]
                                          ).concat(
                                            chains as {
                                              id: string;
                                              caspId: string;
                                              chain: string;
                                            }[],
                                          );

                                          nextChains.push({
                                            id: `chain-${nextChains.length + 1}`,
                                            caspId: '',
                                            chain: '',
                                          });

                                          setChains(nextChains);
                                        }}
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                        {!caspChainsIsLoading &&
                          addChainIsVisible &&
                          activePermissions?.includes(
                            PermissionEnum.PSP_INTEGRATION_CASPS_CHAINS_CREATE_ACTION,
                          ) && (
                            <ButtonPrimary
                              size={ButtonSize.SMALL}
                              className={cn.applyMappingButton}
                              text={
                                applyChainsMappingInProgress
                                  ? 'Loading...'
                                  : 'Apply mapping'
                              }
                              onClick={onApplyCaspMapping}
                              disabled={
                                !chains?.filter((item) => item.caspId)
                                  ?.length || applyChainsMappingInProgress
                              }
                            />
                          )}
                      </div>
                    )}
                  </>
                )}

                {activePermissions?.includes(
                  PermissionEnum.PSP_INTEGRATION_EXTERNAL_VAULT_SETTINGS_EDIT_ACTION,
                ) &&
                  (selectedPspIntegration?.name === 'OPEN_PAYD' ||
                    selectedPspIntegration?.name === 'PRIME_VAULT_WALLET') && (
                    <>
                      <div className={cn.pspIntegrationsWrapperTitle}>
                        Linking to External Vault
                      </div>
                      <div
                        className={`${cn.integrationBlock} ${cn.smallPadding}`}
                      >
                        <div className={cn.externalVaultSettingsBlock}>
                          <ToggleInput
                            name="externalPspPartnerAccountCreate"
                            title="Manual linking by Vault number"
                            checked={
                              externalVaultSettings?.externalPspPartnerAccountCreate
                            }
                            disabled={updateExternalSettingsIsLoading}
                            onChange={(e) =>
                              onUpdateExternalVaultSettings({
                                ...externalVaultSettings,
                                externalPspPartnerAccountCreate:
                                  e.target.checked,
                              } as ExternalVaultSettings)
                            }
                          />
                          <div className={cn.externalSettingDescription}>
                            When creating a Bank Account, you can linking to an
                            external vault using its number
                          </div>
                        </div>
                        <div
                          className={cn.externalVaultSettingsBlockSeparator}
                        />
                        <div className={cn.externalVaultSettingsBlock}>
                          <ToggleInput
                            name="externalAccountsAutoCreate"
                            title="Auto Vault creation"
                            checked={
                              externalVaultSettings?.externalAccountsAutoCreate
                            }
                            disabled={updateExternalSettingsIsLoading}
                            onChange={(e) => {
                              const body = {
                                ...externalVaultSettings,
                                externalAccountsAutoCreate: e.target.checked,
                              };

                              if (!e.target.checked) {
                                body.externalAccountsAutoCreateCurrencies = [];
                              }

                              onUpdateExternalVaultSettings(
                                body as ExternalVaultSettings,
                              );

                              if (!e.target.checked) {
                                setConfigureCASPCurrenciesVisible(false);
                                setCurrenciesIsRequiredError('');
                                setCurrencyCASPRequiredErrors([]);
                                setGlobalExternalCurrenciesSettingsError('');
                              }
                            }}
                          />
                          <div className={cn.externalSettingDescription}>
                            Creating a bank account automatically generates and
                            links an external Vault
                          </div>
                          {externalVaultSettings?.externalAccountsAutoCreate &&
                            activePermissions?.includes(
                              PermissionEnum.PSP_INTEGRATION_ACCOUNT_AUTO_CREATE_CURRENCIES_EDIT_ACTION,
                            ) && (
                              <div
                                className={cn.externalSettingsCurrenciesWrapper}
                              >
                                <div
                                  className={
                                    cn.externalSettingsCurrenciesDescription
                                  }
                                >
                                  The CASPs available in the integration will be
                                  used when opening accounts
                                </div>
                                <ToggleInput
                                  name="configureCASPCurrenciesVisible"
                                  title="Configure CASPs for the available currencies"
                                  checked={configureCASPCurrenciesVisible}
                                  disabled={updateExternalSettingsIsLoading}
                                  onChange={(e) => {
                                    setConfigureCASPCurrenciesVisible(
                                      e.target.checked,
                                    );

                                    if (
                                      externalVaultSettings
                                        ?.externalAccountsAutoCreateCurrencies
                                        ?.length &&
                                      !e.target.checked
                                    ) {
                                      onUpdateExternalVaultSettings({
                                        ...externalVaultSettings,
                                        externalAccountsAutoCreateCurrencies:
                                          [],
                                      });
                                    }
                                  }}
                                />
                                {configureCASPCurrenciesVisible && (
                                  <div
                                    className={
                                      cn.externalSettingsCurrenciesContent
                                    }
                                  >
                                    <Select
                                      name="settingsCurrency"
                                      title="Currencies"
                                      placeholder="Select Currencies"
                                      selectWrapperClassName={
                                        cn.selectCurrencySetting
                                      }
                                      list={
                                        availableCurrencies.map((item) => ({
                                          name: item.name,
                                          value: item.id,
                                        })) || []
                                      }
                                      onChange={(e) => {
                                        let nextCurrencies: {
                                          currencyId: string;
                                          caspId?: string;
                                          id?: string;
                                        }[] = [];

                                        if (
                                          externalVaultSettings
                                            .externalAccountsAutoCreateCurrencies
                                            ?.length
                                        ) {
                                          nextCurrencies = (
                                            [] as {
                                              currencyId: string;
                                              caspId?: string;
                                              id?: string;
                                            }[]
                                          ).concat(
                                            externalVaultSettings.externalAccountsAutoCreateCurrencies,
                                          );
                                        }

                                        nextCurrencies.push({
                                          currencyId: e.target.value as string,
                                          caspId: '',
                                          id: uuidv4(),
                                        });

                                        onUpdateExternalVaultSettings({
                                          ...externalVaultSettings,
                                          externalAccountsAutoCreateCurrencies:
                                            nextCurrencies,
                                        });

                                        if (currenciesIsRequiredError) {
                                          setCurrenciesIsRequiredError('');
                                        }
                                      }}
                                      error={currenciesIsRequiredError}
                                      disabled={updateExternalSettingsIsLoading}
                                      fullWidth
                                      withoutSelectedValue
                                    />
                                    {externalVaultSettings?.externalAccountsAutoCreateCurrencies &&
                                      externalVaultSettings
                                        ?.externalAccountsAutoCreateCurrencies
                                        ?.length > 0 && (
                                        <div
                                          className={cn.currenciesSettingsList}
                                        >
                                          {externalVaultSettings?.externalAccountsAutoCreateCurrencies?.map(
                                            (item) => (
                                              <CurrencySettingItem
                                                key={item.id}
                                                currencySetting={item}
                                                casp={casp}
                                                currencies={availableCurrencies}
                                                externalVaultSettings={
                                                  externalVaultSettings
                                                }
                                                onUpdateExternalVaultSettings={
                                                  onUpdateExternalVaultSettings
                                                }
                                                currencyCASPRequiredErrors={
                                                  currencyCASPRequiredErrors
                                                }
                                                setCurrencyCASPRequiredErrors={
                                                  setCurrencyCASPRequiredErrors
                                                }
                                                currencyCASPError={currencyCASPRequiredErrors?.find(
                                                  (error) =>
                                                    error.id === item.id,
                                                )}
                                                updateExternalSettingsIsLoading={
                                                  updateExternalSettingsIsLoading
                                                }
                                              />
                                            ),
                                          )}
                                        </div>
                                      )}
                                  </div>
                                )}
                                {globalExternalCurrenciesSettingsError && (
                                  <div
                                    className={
                                      cn.externalCurrenciesSettingsError
                                    }
                                  >
                                    {globalExternalCurrenciesSettingsError}
                                  </div>
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className={cn.feesSettingsFooter}>
                        <ButtonPrimary
                          size={ButtonSize.SMALL}
                          text={
                            updateExternalSettingsIsLoading
                              ? 'Loading...'
                              : 'Save'
                          }
                          onClick={updateExternalSettinsAction}
                          disabled={!linkingSettingsIsDirty}
                        />
                        {linkingSettingsIsDirty && (
                          <ButtonSecondary
                            size={ButtonSize.SMALL}
                            text="Reset"
                            className={cn.resetFeeSettings}
                            onClick={() => {
                              onUpdateExternalVaultSettings({
                                externalAccountsAutoCreate:
                                  selectedPspIntegration?.externalAccountsAutoCreate as boolean,
                                externalPspPartnerAccountCreate:
                                  selectedPspIntegration?.externalPspPartnerAccountCreate as boolean,
                                externalAccountsAutoCreateCurrencies:
                                  selectedPspIntegration?.externalAccountsAutoCreateCurrencies?.map(
                                    (item) => ({
                                      currencyId: item.currencyId,
                                      caspId: item?.caspId,
                                      id: uuidv4(),
                                    }),
                                  ),
                                externalAccountsAutoCreateCurrencyType:
                                  selectedPspIntegration?.externalAccountsAutoCreateCurrencyType,
                              });

                              if (
                                selectedPspIntegration
                                  ?.externalAccountsAutoCreateCurrencies?.length
                              ) {
                                setConfigureCASPCurrenciesVisible(true);
                              }

                              setGlobalExternalCurrenciesSettingsError('');
                              setCurrencyCASPRequiredErrors([]);
                              setCurrenciesIsRequiredError('');
                              setGlobalExternalVaultSettingsError('');
                            }}
                          />
                        )}
                        {globalExternalVaultSettingsError && (
                          <div className={cn.externalVaultSettingsError}>
                            {globalExternalVaultSettingsError}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                {selectedPspIntegration?.name === 'PRIME_VAULT_WALLET' && (
                  <>
                    {activePermissions?.includes(
                      PermissionEnum.PSP_INTEGRATION_FEE_TIER_LIST,
                    ) && (
                      <>
                        <div className={cn.pspIntegrationsWrapperTitle}>
                          Fee settings
                        </div>
                        <div className={cn.feeSettingsWrapper}>
                          <div className={cn.feeSettingsBlockWrapper}>
                            <div className={cn.feeSettingsBlock}>
                              <div className={cn.feeSettingsBlockTitle}>
                                Outgoing Transactions settings
                              </div>
                              {feeSettingsIsLoading && (
                                <div className={cn.feeSettingLoader}>
                                  <IconLoader />
                                </div>
                              )}
                              {!feeSettingsIsLoading && (
                                <>
                                  {!outgoingTransactionsFeeSettings?.length && (
                                    <div className={cn.emptyFeeSettingsBlock}>
                                      <div
                                        className={
                                          cn.emptyFeeSettingsBlockTitle
                                        }
                                      >
                                        No fee settings defined yet
                                      </div>
                                      {activePermissions.includes(
                                        PermissionEnum.PSP_INTEGRATION_FEE_TIER_ADD_ACTION,
                                      ) && (
                                        <div
                                          className={
                                            cn.emptyFeeSettingsBlockButtonWrapper
                                          }
                                        >
                                          <ButtonSecondary
                                            size={ButtonSize.SMALL}
                                            className={`button-white ${cn.addFeeSettingsButton}`}
                                            text="Add fee setting"
                                            icon={<IconExpand />}
                                            onClick={() => {
                                              setFeeSettingsPanelIsOpen(true);
                                              setSelectedTransactionType(
                                                'outgoing',
                                              );
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {outgoingTransactionsFeeSettings &&
                                    outgoingTransactionsFeeSettings?.length >
                                      0 && (
                                      <>
                                        {outgoingTransactionsFeeSettings?.map(
                                          (item) => (
                                            <FeeSettingItem
                                              key={item.id}
                                              feeSettingItem={item}
                                              casp={casp}
                                              accountsList={autocompleteList}
                                              onDeleteClick={() => {
                                                setSelectedFeeTierItemId(
                                                  item.id,
                                                );
                                                setDeleteFeeSettingsPopupVisible(
                                                  true,
                                                );
                                              }}
                                              onEditClick={() => {
                                                setFeeSettingsPanelIsOpen(true);
                                                getFeeTierItemAction(item.id);
                                                setSelectedTransactionType(
                                                  'outgoing',
                                                );
                                              }}
                                              editIsAvailable={activePermissions?.includes(
                                                PermissionEnum.PSP_INTEGRATION_FEE_TIER_EDIT_ACTION,
                                              )}
                                              deleteIsAvailable={activePermissions?.includes(
                                                PermissionEnum.PSP_INTEGRATION_FEE_TIER_DELETE_ACTION,
                                              )}
                                            />
                                          ),
                                        )}
                                        {activePermissions.includes(
                                          PermissionEnum.PSP_INTEGRATION_FEE_TIER_ADD_ACTION,
                                        ) && (
                                          <ButtonSecondary
                                            size={ButtonSize.SMALL}
                                            className={`button-white ${cn.addFeeSettingsButton}`}
                                            text="Add fee setting"
                                            icon={<IconExpand />}
                                            onClick={() => {
                                              setFeeSettingsPanelIsOpen(true);
                                              setSelectedTransactionType(
                                                'outgoing',
                                              );
                                            }}
                                          />
                                        )}
                                      </>
                                    )}
                                </>
                              )}
                            </div>
                          </div>
                          <div className={cn.feeSettingsBlockWrapper}>
                            <div className={cn.feeSettingsBlock}>
                              <div className={cn.feeSettingsBlockTitle}>
                                Nostro Transactions settings
                              </div>
                              {feeSettingsIsLoading && (
                                <div className={cn.feeSettingLoader}>
                                  <IconLoader />
                                </div>
                              )}
                              {!feeSettingsIsLoading && (
                                <>
                                  {!nostroTransactionsFeeSettings?.length && (
                                    <div className={cn.emptyFeeSettingsBlock}>
                                      <div
                                        className={
                                          cn.emptyFeeSettingsBlockTitle
                                        }
                                      >
                                        No fee settings defined yet
                                      </div>
                                      {activePermissions.includes(
                                        PermissionEnum.PSP_INTEGRATION_FEE_TIER_ADD_ACTION,
                                      ) && (
                                        <div
                                          className={
                                            cn.emptyFeeSettingsBlockButtonWrapper
                                          }
                                        >
                                          <ButtonSecondary
                                            size={ButtonSize.SMALL}
                                            className={`button-white ${cn.addFeeSettingsButton}`}
                                            text="Add fee setting"
                                            icon={<IconExpand />}
                                            onClick={() => {
                                              setFeeSettingsPanelIsOpen(true);
                                              setSelectedTransactionType(
                                                'nostro',
                                              );
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {nostroTransactionsFeeSettings &&
                                    nostroTransactionsFeeSettings?.length >
                                      0 && (
                                      <>
                                        {nostroTransactionsFeeSettings?.map(
                                          (item) => (
                                            <FeeSettingItem
                                              key={item.id}
                                              feeSettingItem={item}
                                              casp={casp}
                                              accountsList={autocompleteList}
                                              onDeleteClick={() => {
                                                setSelectedFeeTierItemId(
                                                  item.id,
                                                );
                                                setDeleteFeeSettingsPopupVisible(
                                                  true,
                                                );
                                              }}
                                              onEditClick={() => {
                                                setFeeSettingsPanelIsOpen(true);
                                                getFeeTierItemAction(item.id);
                                                setSelectedTransactionType(
                                                  'nostro',
                                                );
                                              }}
                                              editIsAvailable={activePermissions?.includes(
                                                PermissionEnum.PSP_INTEGRATION_FEE_TIER_EDIT_ACTION,
                                              )}
                                              deleteIsAvailable={activePermissions?.includes(
                                                PermissionEnum.PSP_INTEGRATION_FEE_TIER_DELETE_ACTION,
                                              )}
                                            />
                                          ),
                                        )}
                                        {activePermissions.includes(
                                          PermissionEnum.PSP_INTEGRATION_FEE_TIER_ADD_ACTION,
                                        ) && (
                                          <ButtonSecondary
                                            size={ButtonSize.SMALL}
                                            className={`button-white ${cn.addFeeSettingsButton}`}
                                            text="Add fee setting"
                                            icon={<IconExpand />}
                                            onClick={() => {
                                              setFeeSettingsPanelIsOpen(true);
                                              setSelectedTransactionType(
                                                'nostro',
                                              );
                                            }}
                                          />
                                        )}
                                      </>
                                    )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
            {selectedPspIntegration?.name === 'B2C2' && (
              <div className={cn.caspChainsWrapper}>
                <div className={cn.pspIntegrationsWrapperTitle}>
                  Exchange Rate Tolerance
                </div>
                <div className={cn.caspChains}>
                  <Checkbox
                    name="hasExchangeRateTolerance"
                    title="Set a default Exchange Rate Tolerance"
                    type="checkbox"
                    checked={exchangeRateTolerance?.hasExchangeRateTolerance}
                    onChange={(e) =>
                      setExchangeRateTolerance({
                        exchangeRateTolerance: e.target.checked
                          ? exchangeRateTolerance?.exchangeRateTolerance
                          : null,
                        hasExchangeRateTolerance: e.target.checked,
                      })
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.PSP_INTEGRATION_EXCHANGE_TOLERANCE_EDIT_ACTION,
                      )
                    }
                  />
                  {exchangeRateTolerance?.hasExchangeRateTolerance && (
                    <div className={cn.exchangeRateToleranceWrapper}>
                      <InputNumber
                        name="exchangeRateTolerance"
                        value={
                          exchangeRateTolerance?.exchangeRateTolerance as number
                        }
                        title="Default Tolerance, bp"
                        placeholder="Enter exchange rate tolerance"
                        onChange={(e) => {
                          let tolerance = e.target.value;
                          clearErrors(['exchangeRateTolerance']);

                          if (Number(tolerance) > 20) {
                            tolerance = '20';
                          } else if (tolerance && Number(tolerance) < 0) {
                            tolerance = '0.01';
                          } else if (tolerance && Number(tolerance) === 0) {
                            setToleranceError('exchangeRateTolerance', {
                              type: 'custom',
                              message:
                                'Exchange Rate Tolerance must be 0.01 or higher',
                            });
                          }

                          setExchangeRateTolerance({
                            ...exchangeRateTolerance,
                            exchangeRateTolerance: tolerance,
                          });
                        }}
                        disabled={
                          !activePermissions?.includes(
                            PermissionEnum.PSP_INTEGRATION_EXCHANGE_TOLERANCE_EDIT_ACTION,
                          )
                        }
                        error={toleranceErrors?.exchangeRateTolerance?.message}
                        suffix="bp"
                        suffixClassName={cn.toleranceSuffix}
                      />
                      <div className={cn.toleranceHint}>From 0.01 to 20 bp</div>
                    </div>
                  )}
                  {globalToleranceError && (
                    <div className={cn.toleranceGlobalError}>
                      {globalToleranceError}
                    </div>
                  )}
                </div>
                {activePermissions?.includes(
                  PermissionEnum.PSP_INTEGRATION_EXCHANGE_TOLERANCE_EDIT_ACTION,
                ) && (
                  <ButtonPrimary
                    size={ButtonSize.SMALL}
                    className={cn.applyMappingButton}
                    text={
                      updateExcahngeRateToleranceInProgress
                        ? 'Loading...'
                        : 'Save settings'
                    }
                    onClick={onUpdateExchangeRateTolerance}
                    disabled={updateExcahngeRateToleranceInProgress}
                  />
                )}
              </div>
            )}
            <div className={cn.pspIntegrationsWrapperTitle}>
              Supported payment types, currencies & connections types
            </div>
            <div className={cn.integrationBlock}>
              {selectedPspIntegration.pspIntegrationPayments?.map(
                (item, index) => {
                  const currentPaymentSettings = paymentsSettings?.find(
                    (settings) => settings.pspIntegrationPaymentId === item.id,
                  );

                  return (
                    <Fragment key={item.id}>
                      <div className={cn.paymentTypeTop}>
                        <div className={cn.paymentTypeTitle}>{`Group #${
                          index + 1
                        }`}</div>
                        {integrated && isEditable && (
                          <>
                            {(!currentPaymentSettings ||
                              (currentPaymentSettings &&
                                !currentPaymentSettings.bic &&
                                !currentPaymentSettings.sortCode)) && (
                              <ButtonPseudo
                                size={ButtonSize.SMALL}
                                text="Set BIC or Sort code"
                                className={cn.sortCodeButton}
                                onClick={() => {
                                  setSelectedGroup({
                                    id: item.id,
                                    name: `Group #${index + 1}`,
                                  });

                                  setSortCodePopupVisible(true);

                                  if (currentPaymentSettings) {
                                    setPaymentSettingsId(
                                      currentPaymentSettings.id,
                                    );
                                  }
                                }}
                              />
                            )}
                            {currentPaymentSettings &&
                              (currentPaymentSettings.bic ||
                                currentPaymentSettings.sortCode) && (
                                <div className={cn.paymentSettingsWrapper}>
                                  {currentPaymentSettings.bic && (
                                    <span>{`BIC: ${currentPaymentSettings.bic}`}</span>
                                  )}
                                  {currentPaymentSettings.sortCode && (
                                    <span>{`Sort Code: ${currentPaymentSettings.sortCode}`}</span>
                                  )}
                                  <button
                                    type="button"
                                    className={cn.editPaymentsSettingsButton}
                                    aria-label="edit"
                                    onClick={() => {
                                      setSelectedGroup({
                                        id: item.id,
                                        name: `Group #${index + 1}`,
                                      });

                                      setSortCodePopupVisible(true);

                                      if (currentPaymentSettings) {
                                        setPaymentSettingsId(
                                          currentPaymentSettings.id,
                                        );

                                        if (currentPaymentSettings.bic) {
                                          setBic(currentPaymentSettings.bic);
                                        }

                                        if (currentPaymentSettings.sortCode) {
                                          setSortCode(
                                            currentPaymentSettings.sortCode,
                                          );
                                        }
                                      }
                                    }}
                                  >
                                    <IconEdit width={16} height={16} />
                                  </button>
                                </div>
                              )}
                          </>
                        )}
                      </div>
                      <PspIntegrationItem
                        pspIntegrationPayments={item}
                        paymentTypes={paymentTypes}
                      />
                    </Fragment>
                  );
                },
              )}
            </div>
            {isEditable && (
              <>
                <div className={cn.pspIntegrationHint}>
                  Note: once you save the selected integration type, you will
                  not be able to change it.
                </div>
                <ButtonPrimary
                  size={ButtonSize.SMALL}
                  text={
                    applyInProgress ? 'Loading...' : 'Apply Integration type'
                  }
                  className={cn.applyPspIntegrationButton}
                  onClick={() => setIntegrationPopupVisible(true)}
                />
              </>
            )}
          </div>
        )}
        <Popup
          title="Apply Integration Type?"
          description="The Integration Type is applied once and cannot be changed"
          onCancelClick={() => setIntegrationPopupVisible(false)}
          onOkClick={() => onApplyPspIntegration()}
          cancelButtonText="Cancel"
          okButtonText={applyInProgress ? 'Loading...' : 'Apply'}
          isVisible={integrationPopupVisible}
          isLoading={applyInProgress}
        />
        <Popup
          title={`Enter Sort code or BIC for Payments Types in Group ${selectedGroup?.name}`}
          onCancelClick={() => {
            setSortCodePopupVisible(false);
            setSortCode('');
            setBic('');
            setSelectedGroup(undefined);
            setSortCodeError('');
          }}
          onOkClick={() => {
            if (!paymentSettingsId) {
              onAddPaymentSettings(selectedGroup?.id as string);
            } else {
              onUpdatePaymentSettings({
                settingsId: paymentSettingsId,
                pspIntegrationPaymentId: selectedGroup?.id as string,
              });
            }
          }}
          cancelButtonText="Cancel"
          okButtonText={
            addPaymentSettingsIsLoading || updatePaymentSettingsInProgress
              ? 'Loading...'
              : 'Set Sort code or BIC'
          }
          isVisible={sortCodePopupVisible}
        >
          <>
            <div className={cn.bicWrapper}>
              <InputText
                name="sortCode"
                placeholder="Enter Sort Code"
                value={sortCode}
                onChange={(e) => {
                  setSortCode(e.target.value);

                  if (!e.target.value) {
                    setSortCode('');
                  }
                }}
                onKeyUp={onKeyUp}
                maxLength={8}
                error={sortCodeError}
              />
            </div>
            <div className={cn.bicWrapper}>
              <InputText
                name="bic"
                placeholder="Enter BIC"
                value={bic}
                onChange={(e) => setBic(e.target.value)}
              />
            </div>
          </>
        </Popup>
      </>
    );
  }

  return content;
};

export default PspIntegrations;
